import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  MessageSquare,
  Search,
  BadgeCheck,
  Sparkles,
  ExternalLink,
  UserPlus,
  Copy,
  Loader2,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { MessagesPanel } from "@/components/dashboard/MessagesPanel";
import { FreelancerAvatar } from "@/components/FreelancerAvatar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export const Route = createFileRoute("/_authenticated/chat")({
  head: () => ({
    meta: [
      { title: "Chat — Lumvio" },
      { name: "description", content: "Find freelancers by username and start a secure chat." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ChatPage,
});

type Freelancer = {
  portfolio_username: string;
  full_name: string | null;
  avatar_url: string | null;
  portfolio_headline: string | null;
  portfolio_cover_url: string | null;
  portfolio_skills: string[];
  verified: boolean;
};

function ChatPage() {
  const { user, profile } = useAuth();
  const { t } = useTranslation();

  if (!user || !profile) {
    return <div className="py-24 text-center text-muted-foreground">{t("common.loading")}</div>;
  }

  const mode = profile.role === "freelancer" ? "freelancer" : "client";

  return (
    <div className="space-y-8">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl border border-border/60 bg-card/60 p-6 md:p-8 backdrop-blur-xl">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10 opacity-70"
          style={{
            background:
              "radial-gradient(600px 260px at 10% 0%, color-mix(in oklab, var(--primary) 22%, transparent), transparent 60%), radial-gradient(500px 240px at 90% 20%, color-mix(in oklab, var(--primary) 14%, transparent), transparent 60%)",
          }}
        />
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/30 to-primary/5 text-primary ring-1 ring-primary/20">
            <MessageSquare className="h-6 w-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">{t("chat.title")}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {mode === "client" ? t("chat.pageSubClient") : t("chat.pageSubFreelancer")}
            </p>
          </div>
        </div>
      </div>

      {mode === "client" ? (
        <ClientDiscovery userId={user.id} />
      ) : (
        <FreelancerShareCard username={profile.portfolio_username ?? null} />
      )}

      {/* Threads */}
      <MessagesPanel userId={user.id} mode={mode} email={profile.email} />
    </div>
  );
}

/* --------------------------------- Client --------------------------------- */

function ClientDiscovery({ userId: _userId }: { userId: string }) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const navigate = useNavigate();

  useDebounce(q, 250, setDebounced);

  const { data, isFetching } = useQuery({
    queryKey: ["freelancer-search", debounced],
    queryFn: async (): Promise<Freelancer[]> => {
      const { data, error } = await supabase.rpc("search_freelancers", { _query: debounced });
      if (error) throw error;
      return (data ?? []) as Freelancer[];
    },
    enabled: debounced.trim().length >= 1,
    staleTime: 15_000,
  });

  const [startingFor, setStartingFor] = useState<string | null>(null);

  async function startChatWith(username: string) {
    setStartingFor(username);
    const { data: pid, error } = await supabase.rpc("create_intro_project_from_referral", { _username: username });
    setStartingFor(null);
    if (error) return toast.error(error.message);
    if (!pid) return toast.error(t("chat.couldNotStart"));
    navigate({ to: "/projects/$id/chat", params: { id: pid as string } });
  }

  const results = data ?? [];
  const showResults = debounced.trim().length >= 1;

  return (
    <div className="relative overflow-hidden rounded-3xl border border-border/60 bg-card/50 p-5 md:p-6 backdrop-blur-xl">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="h-4 w-4 text-primary" />
        <div className="text-sm font-semibold">{t("chat.findFreelancer")}</div>
      </div>

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground rtl:left-auto rtl:right-3" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("chat.searchPlaceholder")}
          className="ps-9 rtl:ps-3 rtl:pe-9 h-12 rounded-2xl bg-background/60 border-border/70 focus-visible:ring-primary/40"
        />
      </div>

      {showResults && (
        <div className="mt-5 space-y-2">
          {isFetching && results.length === 0 ? (
            [0, 1, 2].map((i) => <div key={i} className="h-20 animate-pulse rounded-2xl bg-muted/30" />)
          ) : results.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border/60 p-8 text-center">
              <div className="text-3xl">🔎</div>
              <div className="mt-2 text-sm text-muted-foreground">
                {t("chat.noMatch", { q: debounced })}
              </div>
            </div>
          ) : (
            results.map((f) => (
              <FreelancerRow
                key={f.portfolio_username}
                f={f}
                starting={startingFor === f.portfolio_username}
                onStart={() => startChatWith(f.portfolio_username)}
              />
            ))
          )}
        </div>
      )}

      {!showResults && (
        <p className="mt-3 text-xs text-muted-foreground">{t("chat.searchTip")}</p>
      )}
    </div>
  );
}

function FreelancerRow({
  f,
  starting,
  onStart,
}: {
  f: Freelancer;
  starting: boolean;
  onStart: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="group relative flex flex-col gap-3 rounded-2xl border border-border/60 bg-background/50 p-4 transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-[0_20px_50px_-30px_color-mix(in_oklab,var(--primary)_60%,transparent)] sm:flex-row sm:items-center">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <FreelancerAvatar path={f.avatar_url} name={f.full_name || f.portfolio_username} verified={f.verified} size="md" />
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <div className="truncate font-semibold">{f.full_name || `@${f.portfolio_username}`}</div>
            {f.verified && (
              <Badge variant="secondary" className="gap-1 h-5 px-1.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20">
                <BadgeCheck className="h-3 w-3" />
              </Badge>
            )}
          </div>
          <div className="truncate text-xs text-muted-foreground">@{f.portfolio_username}</div>
          {f.portfolio_headline && (
            <div className="mt-0.5 truncate text-xs text-foreground/80">{f.portfolio_headline}</div>
          )}
          {f.portfolio_skills?.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {f.portfolio_skills.slice(0, 4).map((s) => (
                <span key={s} className="rounded-full bg-muted/60 px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                  {s}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 self-stretch sm:self-auto">
        <Button asChild size="sm" variant="ghost" className="gap-1">
          <Link to="/portfolio/$username" params={{ username: f.portfolio_username }}>
            {t("chat.view")} <ExternalLink className="h-3 w-3" />
          </Link>
        </Button>
        <Button size="sm" onClick={onStart} disabled={starting} className="gap-1.5">
          {starting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />}
          {t("chat.startChat")}
        </Button>
      </div>
    </div>
  );
}

/* -------------------------------- Freelancer ------------------------------ */

function FreelancerShareCard({ username }: { username: string | null }) {
  const { t } = useTranslation();
  const url =
    username && typeof window !== "undefined"
      ? `${window.location.origin}/portfolio/${username}`
      : null;

  function copy() {
    if (!url) return;
    navigator.clipboard.writeText(url).then(() => toast.success(t("chat.copied")));
  }

  return (
    <div className="relative overflow-hidden rounded-3xl border border-border/60 bg-card/50 p-5 md:p-6 backdrop-blur-xl">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="h-4 w-4 text-primary" />
        <div className="text-sm font-semibold">{t("chat.yourPublicProfile")}</div>
      </div>

      {username && url ? (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">{t("chat.shareIntro")}</p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="flex min-w-0 flex-1 items-center gap-2 rounded-xl border border-border/60 bg-background/60 px-3 py-2 font-mono text-xs">
              <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate" dir="ltr">{url}</span>
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" onClick={copy} className="gap-1.5">
                <Copy className="h-3.5 w-3.5" /> {t("chat.copy")}
              </Button>
              <Button asChild size="sm" className="gap-1.5">
                <Link to="/portfolio/$username" params={{ username }}>
                  {t("chat.open")} <ExternalLink className="h-3.5 w-3.5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">{t("chat.activatePortfolio")}</p>
          <Button asChild size="sm">
            <Link to="/portfolio">{t("chat.setupPortfolio")}</Link>
          </Button>
        </div>
      )}
    </div>
  );
}

/* --------------------------------- utils --------------------------------- */

function useDebounce<T>(value: T, ms: number, setter: (v: T) => void) {
  useEffect(() => {
    const id = setTimeout(() => setter(value), ms);
    return () => clearTimeout(id);
  }, [value, ms, setter]);
}

